import { CloudUpload, Download, Info } from "lucide-react";
import type React from "react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";

import { Button } from "~/components/ui";
import { type BulkCreateUsersResult, useBulkUserCreate } from "~/features/admin/users/hooks";
import { useRetryUserLoginPreparation } from "~/features/admin/users/hooks/useRetryUserLoginPreparation";

export function UsersBulkUpload() {
  const { t } = useTranslation();
  const [file, setFile] = useState<File | null>(null);
  const [uploadResult, setUploadResult] = useState<BulkCreateUsersResult | null>(null);

  const { submit, processing } = useBulkUserCreate({
    onSuccess: (result) => setUploadResult(result),
  });

  const onFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFile(e.target.files?.[0] ?? null);
  };

  const onUpload = async () => {
    if (!file) return;
    setUploadResult(null);
    await submit(file);
  };

  const downloadTemplate = () => {
    const csvContent = [
      "user_id,name,email,organization,dewey_role,darius_role,board_role,segments,group,memo",
      ",sample name,sampleemail@gmail.com,coten,editor,privilegedViewer,viewer,coten,Chambers,<- sample to add a Chambers member",
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = "UserBulkUpload_Template.csv";
    link.click();
  };

  return (
    <div className="mx-auto flex max-w-[var(--content-max)] flex-col gap-5 p-8">
      <Button type="button" variant="ghost" size="sm" asChild className="-ml-2 self-start">
        <Link to="..">{t("ui.backToList")}</Link>
      </Button>

      <h1 className="text-dewey-title">{t("model.users.bulkCreate.title")}</h1>

      <div className="space-y-6 rounded-[var(--radius-md)] border border-border-default bg-white p-6">
        <div className="flex flex-col items-start gap-4">
          <p className="text-14-regular text-fg-2">{t("model.users.bulkCreate.description")}</p>

          <Button type="button" variant="outline" size="sm" onClick={downloadTemplate}>
            <Download />
            <span>{t("model.users.bulkCreate.downloadTemplate")}</span>
          </Button>
        </div>

        <div className="flex items-start gap-2 rounded-[var(--radius-md)] border border-info/30 bg-info-bg px-3.5 py-2.5 text-14-regular text-info">
          <Info className="mt-0.5 h-4 w-4 shrink-0" />
          <span>{t("model.users.bulkCreate.csvFormatInfo")}</span>
        </div>

        <label className="flex cursor-pointer flex-col items-center justify-center gap-1.5 rounded-[var(--radius-md)] border border-dashed border-border-default bg-bg-subtle px-6 py-10 text-center transition-colors hover:border-primary-400 hover:bg-primary-50">
          <span className="text-14-regular text-fg-2">{t("model.users.bulkCreate.selectFile")}</span>
          <span className="text-12-regular text-fg-3">{t("ui.acceptedFileTypes", { accept: ".csv" })}</span>
          <input type="file" accept=".csv" onChange={onFileChange} className="sr-only" />
        </label>

        {file && (
          <div className="flex items-center justify-between gap-4">
            <p className="text-14-regular text-fg-2">
              {t("model.users.bulkCreate.selectedFile")}: <span className="font-medium text-fg-1">{file.name}</span>
            </p>
            <Button type="button" size="sm" onClick={onUpload} disabled={processing}>
              <CloudUpload />
              <span>{processing ? t("model.users.bulkCreate.uploading") : t("model.users.bulkCreate.upload")}</span>
            </Button>
          </div>
        )}
      </div>

      {uploadResult && <ResultsPanel result={uploadResult} />}
    </div>
  );
}

function ResultsPanel({ result }: { result: BulkCreateUsersResult }) {
  const { t } = useTranslation();
  const { retry, processing } = useRetryUserLoginPreparation();
  const [ready, setReady] = useState<Set<string>>(new Set());
  const savedCount = result.failedUsers.filter((failure) => failure.savedUserId).length;

  return (
    <div className="space-y-4 rounded-[var(--radius-md)] border border-border-default bg-white p-6">
      <h2 className="text-16-bold">{t("model.users.bulkCreate.results")}</h2>

      <div className="flex flex-wrap gap-2">
        <Chip tone="default">
          {t("model.users.bulkCreate.total")}: {result.totalProcessed}
        </Chip>
        <Chip tone="success">
          {t("model.users.bulkCreate.success")}: {result.successCount}
        </Chip>
        {result.failureCount - savedCount > 0 && (
          <Chip tone="danger">
            {t("model.users.bulkCreate.failureCount")}: {result.failureCount - savedCount}
          </Chip>
        )}
      </div>

      {savedCount > 0 && (
        <p className="text-14-regular text-fg-2">
          {t("model.users.bulkCreate.savedRetrySummary", { count: savedCount })}
        </p>
      )}

      {result.successfulUsers.length > 0 && (
        <div className="space-y-2">
          <h3 className="text-14-bold text-fg-1">{t("model.users.bulkCreate.successfulUsers")}</h3>
          <div className="max-h-[300px] overflow-auto rounded-[var(--radius-md)] border border-border-default">
            <table className="w-full border-collapse">
              <thead className="sticky top-0 bg-bg-subtle">
                <tr>
                  <Th>{t("model.user.email")}</Th>
                  <Th>{t("model.common.id")}</Th>
                  <Th>{t("model.users.bulkCreate.note")}</Th>
                </tr>
              </thead>
              <tbody>
                {result.successfulUsers.map((user) => (
                  <tr key={user.id} className="border-t border-border-default">
                    <Td>{user.email}</Td>
                    <Td className="font-mono text-12-regular">{user.id}</Td>
                    <Td className="text-fg-2">{user.note || "-"}</Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {result.failedUsers.length > 0 && (
        <div className="space-y-2">
          <h3 className="text-14-bold text-fg-1">{t("model.users.bulkCreate.attentionUsers")}</h3>
          <div className="max-h-[300px] overflow-auto rounded-[var(--radius-md)] border border-border-default">
            <table className="w-full border-collapse">
              <thead className="sticky top-0 bg-bg-subtle">
                <tr>
                  <Th>#</Th>
                  <Th>{t("model.user.email")}</Th>
                  <Th>{t("model.users.bulkCreate.error")}</Th>
                </tr>
              </thead>
              <tbody>
                {result.failedUsers.map((failure, index) => (
                  <tr key={index} className="border-t border-border-default">
                    <Td className="tabular-nums">{failure.rowNumber}</Td>
                    <Td>{failure.email || t("ui.na")}</Td>
                    <Td className="text-danger">
                      {failure.savedUserId ? (
                        <>
                          <p>{t("model.users.bulkCreate.savedRetryInstruction")}</p>
                          <p className="font-mono text-12-regular">{failure.savedUserId}</p>
                          {ready.has(failure.savedUserId) ? (
                            <p>{t("feature.users.loginPreparationSucceeded")}</p>
                          ) : (
                            <Button
                              type="button"
                              size="sm"
                              disabled={processing}
                              onClick={() => {
                                const id = failure.savedUserId;
                                if (id) {
                                  void retry(id).then((success) => {
                                    if (success) setReady((previous) => new Set(previous).add(id));
                                  });
                                }
                              }}
                            >
                              {t("feature.users.retryLoginPreparation")}
                            </Button>
                          )}
                        </>
                      ) : (
                        failure.error
                      )}
                    </Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

function Chip({ tone, children }: { tone: "default" | "success" | "danger"; children: React.ReactNode }) {
  const toneClass =
    tone === "success"
      ? "bg-success/10 text-success"
      : tone === "danger"
        ? "bg-danger/10 text-danger"
        : "bg-bg-subtle text-fg-2";
  return (
    <span className={`inline-flex items-center rounded-[var(--radius-pill)] px-2.5 py-0.5 text-12-medium ${toneClass}`}>
      {children}
    </span>
  );
}

function Th({ children }: { children: React.ReactNode }) {
  return (
    <th className="border-b border-border-default px-3.5 py-2.5 text-left text-10-regular uppercase tracking-[0.06em] text-fg-2">
      {children}
    </th>
  );
}

function Td({ children, className }: { children: React.ReactNode; className?: string }) {
  return <td className={`px-3.5 py-2 text-14-regular align-middle text-fg-1 ${className ?? ""}`}>{children}</td>;
}
